import type * as OT from '@opentok/client';
import {
  type PublisherProperties,
  type GetUserMediaProperties,
  type VideoFilter,
} from '@opentok/client';
import { getGrowthBookConfigLoader } from '@doxyme/config';
import { CaughtOTError } from './error';
import { getCurrentImageFromPublisher } from './image';
import { type ImageSize } from '../image';
import {
  type CameraId,
  type MicrophoneId,
  type MediaAudioConstraints,
  type VirtualBackgroundFilter,
  type SelectedDevicesChangeEvent,
  type MediaSettings,
} from './media.types';
import {
  MediaEventType,
  FacingMode,
  MediaAccessError,
  VideoQualityProfiles,
  VideoQualityProfilesWide,
} from './media.constants';
import type {
  PublisherSettings,
  Publisher,
  OTVideoDisableReason,
} from './types';
import { getDefaultVideoQuality } from '../../shared/deviceCapabilities/getDefaultVideoQuality';
import {
  MediaAccessStatus,
  VideoQuality,
  VideoQualityConstraint,
} from './media.constants';
import { convertToVideoQualityConstraint } from './convertToVideoQualityConstraint';
import { Devices } from '../devices';
import { _getLogger } from '@doxyme/analytics';
import {
  AspectRatio,
  CurrentAccessError,
  CurrentAccessStatus,
  FireEvent,
  MediaAccessDeniedEvent,
  MediaEventData,
  SpeakerId,
} from './media.types';
import { CustomEmitter } from '../utils/emitter';

export type GetMediaSetStatusType = <T>(
  settings: MediaSettings,
  getMedia: () => Promise<T>,
  getError: (error: unknown) => MediaAccessError,
) => Promise<T>;

const onOff = (b: boolean): string => (b ? 'on' : 'off');

export const getIsDynamicBitrateEnabled = (): boolean =>
  getGrowthBookConfigLoader().getOptional(
    'feat_prod_2440_dynamic_max_bitrate',
  ) === true;

export class Media extends CustomEmitter<MediaEventType, MediaEventData> {
  private opentokClient?: Promise<typeof OT>;

  /**
   * The Publisher object provides the mechanism through which control of the published stream is accomplished.
   * https://tokbox.com/developer/sdks/js/reference/Publisher.html
   */
  private publisher: Publisher | undefined;

  private screenPublisher: Publisher | undefined;

  private isPublisherInitializing = false;

  private readonly logger = _getLogger('MediaVonage');

  private audioListenerTrack?: MediaStreamTrack;

  private readonly onAudioMute = (): void => {
    this.logger.warn('[MediaVonage/audioSource]', 'audio was muted');
    this.fire([{ type: MediaEventType.MediaStreamAudioTrackUpdated }]);
  };

  private readonly onAudioUnmute = (): void => {
    this.logger.warn('[MediaVonage/audioSource]', 'audio was unmuted');
    this.fire([{ type: MediaEventType.MediaStreamAudioTrackUpdated }]);
  };

  private publisherSettings: PublisherSettings = {
    useMicrophone: false,
    useCamera: false,
  };

  private videoAccessStatus?: MediaAccessStatus;

  private audioAccessStatus?: MediaAccessStatus;

  private videoAccessError?: MediaAccessError;

  private audioAccessError?: MediaAccessError;

  private readonly devicesInstance: Devices = new Devices(this.fire.bind(this));

  private readonly unsubscribeList?: (() => void)[] = [];

  private initPromise: Promise<void> | undefined = undefined;

  private startMediaPromise: Promise<void> | undefined = undefined;

  private appliedVirtualBackground: VirtualBackgroundFilter | null = null;

  private backgroundObjectUrl: string | undefined = undefined;

  constructor() {
    super();

    if (typeof window === 'undefined') return; // SSR guard

    this.opentokClient = import('@opentok/client').then((client) => {
      void import('./logGlobalExceptions');
      return client;
    });
  }

  async init(): Promise<void> {
    this.initPromise =
      this.initPromise ??
      (async (): Promise<void> => {
        await this.devicesInstance.init();

        this.addEventListeners();
      })();

    void this.handlePermissionChanges();

    return this.initPromise;
  }

  private async convertSettings(
    mediaSettings: MediaSettings,
    publisherSettings: PublisherSettings,
  ): Promise<PublisherSettings> {
    let videoFilter = publisherSettings.videoFilter;
    if (mediaSettings.virtualBackgroundFilter !== undefined) {
      videoFilter = await this.getVonageFilterFromOurOne(
        mediaSettings.virtualBackgroundFilter,
      );
      this.appliedVirtualBackground = videoFilter
        ? mediaSettings.virtualBackgroundFilter
        : null;
    }

    return {
      ...publisherSettings,
      useMicrophone: Boolean(mediaSettings.audio),
      useCamera: Boolean(mediaSettings.video),
      publishAudio:
        publisherSettings.publishAudio ?? Boolean(mediaSettings.audio),
      publishVideo:
        publisherSettings.publishVideo ?? Boolean(mediaSettings.video),
      videoQuality:
        mediaSettings.videoQuality ??
        publisherSettings.videoQuality ??
        getDefaultVideoQuality(),
      aspectRatio:
        mediaSettings.aspectRatio ?? publisherSettings.aspectRatio ?? '16:9',
      videoFilter,
    };
  }

  private async createPublisher(mediaSettings: MediaSettings): Promise<void> {
    this.publisherSettings = await this.convertSettings(
      mediaSettings,
      this.publisherSettings,
    );

    const {
      useCamera,
      publishVideo,
      useMicrophone,
      publishAudio,
      videoFilter,
      audioFilter,
    } = this.publisherSettings;

    const { frameRate, resolution } = this.getCurrentVideoConstraints();

    const { cameraId, microphoneId } = this.devicesInstance.selected;

    if (!Boolean(useCamera) && !Boolean(useMicrophone)) {
      this.logger.warn(
        '[MediaVonage/createPublisher] nothing to publish. Aborting creation',
      );

      return;
    }

    const opentok = await this.opentokClient!;

    await new Promise<void>((resolve, reject) => {
      const publishProperties: PublisherProperties = {
        audioFallback: { publisher: true, subscriber: true },
        showControls: false,
        insertDefaultUI: false,
        /**
         * Keep the published stream un-mirrored so background-replacement
         * filters (e.g. branded virtual backgrounds) composite in their true
         * orientation and read correctly for subscribers.
         * @see https://tokbox.com/developer/guides/audio-video/js/#mirror
         */
        mirror: false,
        /**
         * This will only start/stop working when captions are enabled/disabled for the session via startLiveCaptions/stopLiveCaptions API
         */
        publishCaptions: true,
        publishAudio,
        publishVideo,
        frameRate,
        resolution,
        disableAudioInputDeviceManagement: true,
        videoFilter,
        audioFilter,
      };

      if (useCamera) {
        publishProperties.videoSource = Boolean(cameraId)
          ? cameraId
          : undefined;
      } else {
        publishProperties.videoSource = null;
      }

      if (useMicrophone) {
        publishProperties.audioSource = Boolean(microphoneId)
          ? microphoneId
          : undefined;
      } else {
        publishProperties.audioSource = null;
      }

      this.logger.debug(
        '[MediaVonage/initPublisher] publishProperties',
        publishProperties,
      );

      const publisher = opentok.initPublisher(
        undefined,
        publishProperties,
        (error) => {
          if (error) {
            if (
              error.name === 'OT_USER_MEDIA_ACCESS_DENIED' ||
              error.name === 'OT_HARDWARE_UNAVAILABLE'
            ) {
              this.publisherSettings.useCamera = false;
              this.publisherSettings.useMicrophone = false;

              this.logger.warn('[MediaVonage/initPublisher]', error);
            } else {
              this.logger.error('[MediaVonage/initPublisher]', error);
            }
            reject(error);
          } else {
            const events = [
              { type: MediaEventType.MediaStatusChange },
              { type: MediaEventType.MediaStreamVideoTrackUpdated },
              { type: MediaEventType.MediaStreamAudioTrackUpdated },
              { type: MediaEventType.VideoElementUpdated },
            ];

            this.publisher = publisher;

            this.fire(events);

            const appliedResolution = convertToVideoQualityConstraint(
              this.publisher.getVideoSource().track?.getConstraints(),
            )?.resolution;
            const currentResolution =
              this.getCurrentVideoConstraints().resolution;

            this.logger.info('[MediaVonage/initPublisher] done', {
              appliedResolution,
              publisherSettings: this.publisherSettings,
            });

            /**
             * Race condition handling for video quality settings:
             * When setVideoQuality is called during publisher initialization,
             * the new resolution will not be applied.
             *
             * Example scenario in Firefox:
             * 1. startLocalMedia begins (waits for user permissions)
             * 2. setVideoQuality(ld) is called
             * 3. User grants permissions and startLocalMedia completes, but resolution isn't applied
             *
             * Solution: Reapply resolution if it's different from the current one after publisher initialization completes
             */
            if (appliedResolution && appliedResolution !== currentResolution) {
              this.changeVideoConstraints();

              this.logger.warn(
                '[MediaVonage/initPublisher] resolution mismatch - reapplying',
                {
                  appliedResolution,
                  currentResolution,
                },
              );
            }

            resolve();
          }
        },
      );

      const prefix = '[MediaVonage/publisher]';

      /**
       * Events relating to media device access.
       */
      publisher.on('accessDialogOpened', () => {
        this.logger.info(prefix, 'accessDialogOpened');
      });
      publisher.on('audioAcquisitionProblem', (event) => {
        this.logger.warn(prefix, 'audioAcquisitionProblem', event);
      });
      publisher.on('accessDialogClosed', () => {
        this.logger.info(prefix, 'accessDialogClosed');
      });
      publisher.on('accessAllowed', (event) => {
        this.logger.info(prefix, 'accessAllowed', event);
        void this.getMediaSetStatus(
          {
            video: useCamera,
            audio: useMicrophone,
          },
          async () => {
            /* empty on purpose */
          },
          (_error) => {
            return MediaAccessError.Error;
          },
        );
        this.fire([{ type: MediaEventType.DevicesChange }]);
      });
      publisher.on('accessDenied', async (event) => {
        this.logger.info(prefix, 'accessDenied', event);

        /**
         * Performs an additional check for device access.
         * Vonage throws an error if both camera and microphone access are blocked
         * or if only the microphone is blocked (even if the camera is allowed).
         */
        const [cameraPermission, micPermission] = await Promise.all([
          navigator.permissions.query({ name: 'camera' as PermissionName }),
          navigator.permissions.query({ name: 'microphone' as PermissionName }),
        ]);

        this.fire([
          {
            type: MediaEventType.MediaAccessDenied,
            data: {
              audio: micPermission.state !== 'granted',
              video: cameraPermission.state !== 'granted',
            },
          },
        ]);
      });
      /**
       * Events relating to Vonage stream lifecycle.
       * @see https://tokbox.com/developer/sdks/js/reference/StreamEvent.html
       */
      publisher.on('streamCreated', (event) => {
        this.logger.info(prefix, 'streamCreated', {
          vonageStreamId: event.stream.streamId,
        });
      });
      publisher.on('streamDestroyed', (event) => {
        this.logger.info(prefix, 'streamDestroyed', {
          vonageStreamId: event.stream.streamId,
          reason: event.reason,
        });

        // @TODO: Need to check what "reset" means. Documentation does not say much. https://tokbox.com/developer/sdks/js/reference/StreamEvent.html
        if (event.reason === 'reset') {
          event.preventDefault();
        }

        if (event.reason === 'mediaStopped') {
          void this.askPermissionsAndRestart();
        }
      });

      /**
       * Miscellaneous events.
       */
      publisher.on('mediaStopped', (event): void => {
        this.logger.info(prefix, 'mediaStopped', {
          kind: event.track?.kind,
          sourceSettings: event.track?.getSettings(),
          sourceConstraints: event.track?.getConstraints(),
          publisherSettings: this.publisherSettings,
        });

        /**
         * If track is undefined, this means the publisher most likely will be destroyed.
         * Possible reasons:
         *  - User denied access to the camera/microphone. The SDK frequently destroys the publisher when camera access is denied
         *  - User placed the tab in the background on mobile and the browser stopped the media
         *  - Current device was used as camera & microphone and was disconnected
         */
        if (event.track === undefined) {
          // Ignore the event most likely the publisher will be destroyed
        }

        /**
         * If audio media is stopped, usually the stream is still live with video,
         * so we automatically switch to another microphone.
         */
        if (event.track?.kind === 'audio') {
          this.fire([
            { type: MediaEventType.MediaStatusChange },
            { type: MediaEventType.MediaStreamAudioTrackUpdated },
          ]);
        }

        /**
         * If video media is stopped, we do not switch to another camera due to privacy.
         */
        if (event.track?.kind === 'video') {
          this.fire([
            { type: MediaEventType.MediaStatusChange },
            { type: MediaEventType.MediaStreamVideoTrackUpdated },
          ]);
        }

        if (event.track?.kind === 'screen') {
          this.fire([
            { type: MediaEventType.MediaStatusChange },
            { type: MediaEventType.MediaStreamScreenTrackUpdated },
          ]);
        }
      });

      publisher.on('videoElementCreated', () => {
        this.attachAudioListeners(publisher.getAudioSource());
      });

      /**
       * @see https://tokbox.com/developer/sdks/js/reference/VideoEnabledChangedEvent.html
       */
      publisher.on('videoEnabled', (event) => {
        const reason = event.reason as OTVideoDisableReason;

        this.logger.info(prefix, 'videoEnabled', {
          reason,
        });

        if (reason === 'quality') {
          void this.audioFallbackDisabled();
        }
      });

      publisher.on('videoDisabled', (event) => {
        const reason = event.reason as OTVideoDisableReason;

        this.logger.info(prefix, 'videoDisabled', {
          reason,
        });

        if (reason === 'quality') {
          void this.audioFallbackEnabled();
        }
      });

      /**
       * @see https://tokbox.com/developer/guides/audio-fallback/js/#enabling_audio_fallback
       */
      publisher.on('videoDisableWarning', (event) => {
        this.logger.info(prefix, 'videoDisableWarning', {
          event,
        });
        void this.videoDisableWarningEnabled();
      });
      publisher.on('videoDisableWarningLifted', (event) => {
        this.logger.info(prefix, 'videoDisableWarningLifted', {
          event,
        });
        void this.videoDisableWarningDisabled();
      });

      /**
       * Events relating to Vonage publisher lifecycle.
       */
      publisher.on('destroyed', (event) => {
        const eventWithReason = event as unknown as { reason: string };
        this.logger.info(prefix, 'destroyed', eventWithReason.reason);

        /**
         * If the publisher is destroyed, we need to set the publisher to undefined
         * to avoid issues with the destroyed publisher being reused. I.e. if the user
         * toggles the audio/video and the publisher is destroyed, the publisher will
         * be reused and the audio/video will not be toggled without any error.
         */
        if (this.publisher === publisher) {
          this.detachAudioListeners();
          this.publisher = undefined;
        }

        void this.audioFallbackDisabled();
        void this.videoDisableWarningDisabled();

        if (eventWithReason.reason === 'mediaStopped') {
          void this.askPermissionsAndRestart();
        } else {
          this.fire([
            { type: MediaEventType.MediaStatusChange },
            { type: MediaEventType.MediaStreamVideoTrackUpdated },
            { type: MediaEventType.MediaStreamAudioTrackUpdated },
          ]);
        }
      });
    });
  }

  private async askPermissions(
    constraints: Pick<GetUserMediaProperties, 'audioSource' | 'videoSource'>,
  ): Promise<boolean> {
    const prefix = '[MediaVonage/askPermissions]';
    try {
      this.logger.info(prefix, 'constraints to getUserMedia', constraints);

      const opentok = await this.opentokClient!;

      const stream = await opentok.getUserMedia(constraints);

      /**
       * After getting the media permission, it starts the tracks.
       * Let's stop the tracks because we don't want to use the camera just yet.
       * @see https://stackoverflow.com/questions/60216430/asking-for-camera-permission-without-turning-the-camera-on
       */
      for (const track of stream.getTracks()) {
        track.stop();
      }

      this.fire([{ type: MediaEventType.MediaAccessStatusChange }]);

      this.logger.info(prefix, 'done');
      return true;
    } catch (error: unknown) {
      if (
        error instanceof Error &&
        (error.name === 'NotAllowedError' ||
          error.name === 'OT_USER_MEDIA_ACCESS_DENIED')
      ) {
        this.fire([
          {
            type: MediaEventType.MediaAccessDenied,
            data: {
              audio: constraints.videoSource === false,
              video: constraints.audioSource === false,
            },
          },
        ]);

        this.logger.warn(prefix, error);
      } else if (
        error instanceof Error &&
        error.name === 'OT_HARDWARE_UNAVAILABLE'
      ) {
        this.logger.warn(prefix, error);
      } else {
        this.logger.error(prefix, error);
      }
      return false;
    }
  }

  private async askPermissionsAndRestart(
    settings?: Partial<Pick<PublisherSettings, 'useCamera' | 'useMicrophone'>>,
  ): Promise<void> {
    if (this.isPublisherInitializing) {
      this.logger.info(
        '[MediaVonage/askPermissionsAndRestart]',
        'ignoring because of starting publisher',
      );

      return;
    }

    this.isPublisherInitializing = true;

    try {
      const { useCamera, useMicrophone } = {
        ...this.publisherSettings,
        ...settings,
      };

      this.logger.info('[MediaVonage/askPermissionsAndRestart]', {
        useCamera,
        useMicrophone,
      });

      let hasCameraAccess = false;
      let hasMicrophoneAccess = false;

      if (useCamera) {
        hasCameraAccess = await this.askPermissions({
          audioSource: false,
          videoSource: undefined,
        });
      }

      if (useMicrophone) {
        hasMicrophoneAccess = await this.askPermissions({
          audioSource: undefined,
          videoSource: false,
        });
      }

      if (!hasCameraAccess && !hasMicrophoneAccess) {
        this.publisherSettings = {
          ...this.publisherSettings,
          publishVideo: false,
          publishAudio: false,
          useCamera: false,
          useMicrophone: false,
        };

        this.fire([
          { type: MediaEventType.MediaAccessStatusChange },
          { type: MediaEventType.MediaStreamAudioTrackUpdated },
          { type: MediaEventType.MediaStreamVideoTrackUpdated },
        ]);
      } else if (useCamera && !hasCameraAccess) {
        this.publisherSettings = {
          ...this.publisherSettings,
          publishVideo: false,
          useCamera: false,
        };
        this.fire([{ type: MediaEventType.MediaStreamVideoTrackUpdated }]);
      } else if (useMicrophone && !hasMicrophoneAccess) {
        this.publisherSettings = {
          ...this.publisherSettings,
          publishAudio: false,
          useMicrophone: false,
        };
        this.fire([{ type: MediaEventType.MediaStreamAudioTrackUpdated }]);
      }

      if (hasCameraAccess) {
        await this.devicesInstance.updatedAccessStatus();
        const currentCamera = this.publisher?.getVideoSource()?.deviceId;
        if (
          currentCamera &&
          this.devicesInstance.selected.cameraId !== currentCamera
        ) {
          this.publisherSettings.publishVideo = false;
        }
      }

      await this.start({
        video: hasCameraAccess,
        audio: hasMicrophoneAccess,
      });

      this.logger.info('[MediaVonage/askPermissionsAndRestart] done');
    } catch (error: unknown) {
      this.logger.error('[MediaVonage/askPermissionsAndRestart]', error);
      throw error;
    } finally {
      this.isPublisherInitializing = false;
    }
  }

  get selectedCameraId(): CameraId | undefined {
    return this.devicesInstance.selected.cameraId;
  }

  get selectedMicrophoneId(): MicrophoneId | undefined {
    return this.devicesInstance.selected.microphoneId;
  }

  get selectedSpeakerId(): SpeakerId | undefined {
    return this.devicesInstance.selected.speakerId;
  }

  get devices(): MediaDeviceInfo[] {
    return this.devicesInstance.devices;
  }

  get cameras(): InputDeviceInfo[] {
    /**
     * @TODO Type assertion as window.navigator.mediaDevices.enumerateDevices() returns MediaDeviceInfo[]
     * likely because this is a new feature in 2024 and types haven't been updated yet
     * https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack/getCapabilities
     */
    return this.devicesInstance.cameras as InputDeviceInfo[];
  }

  get microphones(): InputDeviceInfo[] {
    /**
     * @TODO Type assertion as window.navigator.mediaDevices.enumerateDevices() returns MediaDeviceInfo[]
     * likely because this is a new feature in 2024 and types haven't been updated yet
     * https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack/getCapabilities
     */
    return this.devicesInstance.microphones as InputDeviceInfo[];
  }

  get speakers(): MediaDeviceInfo[] {
    return this.devicesInstance.speakers;
  }

  get hasCameras(): boolean {
    return this.devicesInstance.hasCameras;
  }

  get hasMicrophones(): boolean {
    return this.devicesInstance.hasMicrophones;
  }

  get accessStatus(): CurrentAccessStatus {
    return { video: this.videoAccessStatus, audio: this.audioAccessStatus };
  }

  get accessError(): CurrentAccessError {
    return { video: this.videoAccessError, audio: this.audioAccessError };
  }

  get hasVideoAccess(): boolean {
    return this.videoAccessStatus !== undefined
      ? this.videoAccessStatus === MediaAccessStatus.Allowed
      : this.devicesInstance.cameras.length > 0;
  }

  get hasAudioAccess(): boolean {
    return this.audioAccessStatus !== undefined
      ? this.audioAccessStatus === MediaAccessStatus.Allowed
      : this.devicesInstance.microphones.length > 0;
  }

  get videoElement(): HTMLElement | undefined {
    if (!this.publisher) {
      return undefined;
    }

    if (!this.publisher.videoElement) {
      throw new Error(
        'OpenTok no longer supports Publisher#videoElement(). To avoid problems, use another method to obtain the videoElement.',
      );
    }

    return this.publisher.videoElement();
  }

  get screenElement(): HTMLElement | undefined {
    if (!this.screenPublisher) {
      return undefined;
    }

    if (!this.screenPublisher.videoElement) {
      throw new Error(
        'OpenTok no longer supports Publisher#videoElement(). To avoid problems, use another method to obtain the videoElement.',
      );
    }

    return this.screenPublisher?.videoElement();
  }

  get hasScreenAudio(): boolean {
    return Boolean(this.screenPublisher?.getAudioSource());
  }

  get source(): Publisher | undefined {
    return this.publisher;
  }

  get screenShareSource(): Publisher | undefined {
    return this.screenPublisher;
  }

  get hasVideo(): boolean {
    return Boolean(this.videoTrack?.enabled);
  }

  get hasAudio(): boolean {
    return (
      Boolean(this.audioTrack?.enabled) &&
      !Boolean(this.audioTrack?.muted) &&
      Boolean(this.audioTrack?.readyState !== 'ended')
    );
  }

  /**
   * True when the user has mic publishing enabled but the OS/hardware has muted
   * the capture device (MediaStreamTrack `mute` event)
   */
  get isAudioMutedBySystem(): boolean {
    const track = this.audioTrack;

    if (!track || track.readyState === 'ended') {
      return false;
    }

    return (
      Boolean(this.publisherSettings.publishAudio) &&
      Boolean(track.enabled) &&
      Boolean(track.muted)
    );
  }

  get isVideoAccessBlocked(): boolean {
    return this.videoAccessStatus
      ? [MediaAccessStatus.Denied, MediaAccessStatus.Error].includes(
          this.videoAccessStatus,
        )
      : false;
  }

  get isAudioAccessBlocked(): boolean {
    return this.audioAccessStatus
      ? [MediaAccessStatus.Denied, MediaAccessStatus.Error].includes(
          this.audioAccessStatus,
        )
      : false;
  }

  get volumeLevel(): number | undefined {
    return undefined;
  }

  get isRearCameraUsed(): boolean {
    return this.videoTrack?.getSettings().facingMode === FacingMode.Environment;
  }

  private get videoTrack(): MediaStreamTrack | undefined {
    return this.publisher?.getVideoSource()?.track ?? undefined;
  }

  private get audioTrack(): MediaStreamTrack | undefined {
    return this.publisher?.getAudioSource();
  }

  private get hasVideoSource(): boolean {
    const videoSource = this.publisher?.getVideoSource();
    return Boolean(videoSource?.deviceId || videoSource?.type);
  }

  private get hasAudioSource(): boolean {
    return Boolean(this.publisher?.getAudioSource());
  }

  get tracks(): MediaStreamTrack[] {
    return [this.videoTrack, this.audioTrack].filter(
      Boolean,
    ) as MediaStreamTrack[];
  }

  async stopLocalMedia(): Promise<void> {
    this.logger.info('[MediaVonage/stopLocalMedia]');

    /**
     * IF no publisher, media is already stopped or not initialized yet.
     */
    if (!this.publisher) {
      return;
    }

    try {
      this.detachAudioListeners();
      this.releaseBackgroundObjectUrl();
      this.appliedVirtualBackground = null;
      this.publisher?.destroy();
      this.publisher = undefined;

      this.fire([
        { type: MediaEventType.VideoElementUpdated },
        { type: MediaEventType.MediaStatusChange },
        { type: MediaEventType.MediaStreamAudioTrackUpdated },
        { type: MediaEventType.MediaStreamVideoTrackUpdated },
        { type: MediaEventType.TracksRemoved, data: { tracks: this.tracks } },
      ]);
    } catch (_error: unknown) {
      this.logger.error('[MediaVonage/stopLocalMedia]', _error);
    }
  }

  async toggleVideo(hasVideo = !this.hasVideo): Promise<void> {
    const prefix = '[MediaVonage/toggleVideo]';

    if (this.isPublisherInitializing) {
      this.logger.info(prefix, 'ignoring because of starting publisher');

      return;
    }

    const videoSource = this.devicesInstance.selected.cameraId;
    this.publisherSettings.publishVideo = hasVideo;

    this.logger.info(
      prefix,
      `${onOff(hasVideo)} (currently ${onOff(this.hasVideo)})`,
      {
        useCamera: this.publisherSettings.useCamera,
        videoSource,
      },
    );

    if (this.publisherSettings.useCamera === false) {
      this.logger.warn('publisher was created without camera. Restarting');

      return this.askPermissionsAndRestart({
        useCamera: true,
      });
    }

    if (hasVideo && !this.hasVideoSource) {
      this.logger.warn(prefix, 'publisher has no video source. Restarting');
      return this.askPermissionsAndRestart({ useCamera: true });
    }

    const publisher = this.publisher;
    if (publisher) {
      await new Promise<void>((resolve, reject) => {
        publisher.publishVideo(hasVideo, (otError) => {
          // If publisher was destroyed or replaced during the async operation, ignore the result
          if (this.publisher !== publisher) {
            return resolve();
          }
          if (otError) {
            const error = new CaughtOTError(otError);
            this.logger.error(prefix, error);
            return reject(error);
          }
          resolve();
        });
      });
    }

    if (hasVideo && !this.hasVideo) {
      this.logger.warn(
        prefix,
        'video is missing after toggleVideo. Restarting',
      );

      return this.askPermissionsAndRestart({
        useCamera: true,
      });
    }

    if (hasVideo) {
      this.changeVideoConstraints();
    }

    this.fire([
      { type: MediaEventType.MediaStatusChange },
      { type: MediaEventType.MediaStreamVideoTrackUpdated },
    ]);

    this.logger.info(prefix, 'done');
  }

  async toggleAudio(hasAudio = !this.hasAudio): Promise<void> {
    const prefix = '[MediaVonage/toggleAudio]';

    if (this.isPublisherInitializing) {
      this.logger.info(prefix, 'ignoring because of starting publisher');

      return;
    }

    const audioSource = this.devicesInstance.selected.microphoneId;
    this.publisherSettings.publishAudio = hasAudio;

    this.logger.info(
      prefix,
      `${onOff(hasAudio)} (currently ${onOff(this.hasAudio)})`,
      { audioSource, useMicrophone: this.publisherSettings.useMicrophone },
    );

    if (this.publisherSettings.useMicrophone === false) {
      this.logger.warn('publisher was created without microphone. Restarting');

      return this.askPermissionsAndRestart({
        useMicrophone: true,
      });
    }

    /**
     * Avoid triggering Vonage OT_NOT_SUPPORTED exceptions when the publisher was created without an audio source
     */
    if (hasAudio && !this.hasAudioSource) {
      this.logger.warn(prefix, 'publisher has no audio source. Restarting');
      return this.askPermissionsAndRestart({ useMicrophone: true });
    }

    this.publisher?.publishAudio(hasAudio);

    if (hasAudio) {
      const audioTrack = this.publisher?.getAudioSource();

      if (
        !audioTrack ||
        audioTrack.readyState === 'ended' ||
        audioTrack.muted
      ) {
        this.logger.warn(
          prefix,
          `audio is missing after toggleAudio (track: ${audioTrack?.id}, readyState: ${audioTrack?.readyState}, muted: ${audioTrack?.muted}). Restarting`,
        );
        return this.askPermissionsAndRestart({ useMicrophone: true });
      }

      if (audioTrack?.enabled === false) {
        this.logger.warn(prefix, `audio is disabled`);
      }

      this.attachAudioListeners(audioTrack);
    }

    this.fire([
      { type: MediaEventType.MediaStatusChange },
      { type: MediaEventType.MediaStreamAudioTrackUpdated },
    ]);

    this.logger.info(prefix, 'done');
  }

  private async changeVideoConstraints(
    constraints: VideoQualityConstraint = this.getCurrentVideoConstraints(),
  ): Promise<void> {
    try {
      const currentFilter = this.publisher?.getVideoFilter();

      if (currentFilter) {
        await this.publisher?.clearVideoFilter();
      }

      try {
        const [width, height] = constraints.resolution.split('x').map(Number);

        await this.videoTrack?.applyConstraints({
          width,
          height,
          frameRate: constraints.frameRate,
        });

        const actualResolution = convertToVideoQualityConstraint(
          this.videoTrack?.getSettings(),
        )?.resolution;

        if (actualResolution !== constraints.resolution) {
          this.logger.warn(
            '[MediaVonage/changeVideoConstraints] actual resolution does not match the given quality.',
            {
              actualResolution,
              constraintsResolution: constraints.resolution,
            },
          );
        }

        if (getIsDynamicBitrateEnabled()) {
          await this.publisher?.setMaxVideoBitrate?.(constraints.bitrate);
        }
      } finally {
        if (currentFilter) {
          await this.publisher?.applyVideoFilter(currentFilter);
        }
        this.fire([{ type: MediaEventType.MediaStreamVideoTrackUpdated }]);
      }
    } catch (error) {
      this.logger.error(
        '[MediaVonage/changeVideoConstraints]',
        constraints,
        error,
      );

      throw error;
    }
  }

  async changeAudioConstraints(
    _constraints: MediaAudioConstraints,
  ): Promise<void> {
    // TODO We have to change the constraints on the publisher.
    throw new Error('not implemented yet');
  }

  private async _changeCamera(cameraId: CameraId): Promise<void> {
    const prefix = '[MediaVonage/_changeCamera]';
    this.logger.info(prefix, 'camera id:', cameraId);

    if (!Boolean(cameraId)) {
      this.logger.warn(prefix, 'no camera id. Skipping.');
      return;
    }

    try {
      /**
       * Avoid triggering Vonage OT_NOT_SUPPORTED exceptions when the publisher was created without a video source
       */
      if (!this.hasVideoSource) {
        this.logger.warn(prefix, 'publisher has no video source. Skipping');
        return;
      }

      await this.publisher?.setVideoSource(cameraId);

      this.changeVideoConstraints();

      this.fire([{ type: MediaEventType.MediaStreamVideoTrackUpdated }]);

      this.logger.info(prefix, 'done');
    } catch (error) {
      this.logger.error(prefix, error);
    }
  }

  async changeCamera(cameraId: CameraId): Promise<void> {
    this.logger.info('[MediaVonage/changeCamera] camera id:', cameraId);

    if (cameraId === this.selectedCameraId) {
      return;
    }
    await this._changeCamera(cameraId);
    this.devicesInstance.setCamera(cameraId);
  }

  async flipCamera(): Promise<void> {
    if (this.devicesInstance.cameras.length <= 1) {
      return;
    }

    // Toggle between front ('user') and back ('environment') cameras
    const targetFacing = this.isRearCameraUsed
      ? FacingMode.User
      : FacingMode.Environment;

    try {
      // Find a camera that supports the target facing mode using getCapabilities()
      const targetCamera = this.cameras.find((camera) => {
        try {
          const capabilities = camera.getCapabilities?.();
          return capabilities?.facingMode?.includes(targetFacing);
        } catch {
          return false;
        }
      });

      if (targetCamera && targetCamera.deviceId !== this.selectedCameraId) {
        await this.changeCamera(targetCamera.deviceId as CameraId);
      } else {
        this.logger.warn(
          '[media]flipCamera: no camera found for target facing mode',
          targetFacing,
        );
      }
    } catch (error) {
      this.logger.warn('[media]flipCamera: facingMode switch failed', error);
    }
  }

  /**
   * Detaches the audio source listeners from the audio track.
   */
  private detachAudioListeners(): void {
    this.audioListenerTrack?.removeEventListener('mute', this.onAudioMute);
    this.audioListenerTrack?.removeEventListener('unmute', this.onAudioUnmute);
    this.audioListenerTrack = undefined;
  }

  /**
   * Wires `mute`/`unmute` on the given publisher audio track, replacing any
   * prior wiring via `removeEventListener`.
   */
  private attachAudioListeners(audioTrack: MediaStreamTrack | undefined): void {
    if (this.audioListenerTrack === audioTrack || !audioTrack) {
      return;
    }

    this.detachAudioListeners();

    this.audioListenerTrack = audioTrack;
    audioTrack.addEventListener('mute', this.onAudioMute);
    audioTrack.addEventListener('unmute', this.onAudioUnmute);
  }

  private async _changeMicrophone(microphoneId: MicrophoneId): Promise<void> {
    const prefix = '[MediaVonage/_changeMicrophone]';

    this.logger.info(prefix, 'id:', microphoneId);

    if (!Boolean(microphoneId)) {
      this.logger.warn(prefix, 'no microphone id. Skipping.');
      return;
    }

    /**
     * Avoid triggering Vonage OT_NOT_SUPPORTED exceptions when the publisher was created without an audio source
     */
    if (!this.hasAudioSource) {
      this.logger.warn(prefix, 'publisher has no audio source. Skipping.');
      return;
    }

    try {
      await this.publisher?.setAudioSource(microphoneId);

      this.attachAudioListeners(this.publisher?.getAudioSource());

      this.fire([{ type: MediaEventType.MediaStreamAudioTrackUpdated }]);

      this.logger.info(prefix, 'done');
    } catch (error) {
      this.logger.error(prefix, 'new id:', microphoneId, error);
    }
  }

  async changeMicrophone(microphoneId: MicrophoneId): Promise<void> {
    this.logger.info('[MediaVonage/changeMicrophone] camera id:', microphoneId);

    if (microphoneId === this.selectedMicrophoneId) {
      return;
    }
    await this._changeMicrophone(microphoneId);
    this.devicesInstance.setMicrophone(microphoneId);
  }

  async changeSpeaker(speakerId: SpeakerId): Promise<void> {
    if (speakerId === this.selectedSpeakerId) {
      return;
    }
    this.devicesInstance.setSpeaker(speakerId);
  }

  async getImageFromVideo(
    imageSize?: ImageSize,
  ): Promise<ArrayBuffer | undefined> {
    const prefix = '[MediaVonage/getImageFromVideo]';
    const logger = this.logger;

    const { publisher } = this;
    if (!publisher) {
      return undefined;
    }

    for (let i = 0; i < 3; i++) {
      try {
        const result = await getCurrentImageFromPublisher(publisher, imageSize);
        if (result) {
          logger.info(prefix, 'done');

          return result;
        } else {
          logger.warn(prefix, 'not ready');

          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      } catch (error) {
        logger.error(prefix, error);
      }
    }
    return undefined;
  }

  /**
   * Stops any existing publisher and creates a new one from the given settings.
   * If no settings provided - audio and video are enabled.
   * @param {MediaSettings} customSettings - Custom settings to override default values
   * @returns {Promise<void>}
   */
  async startLocalMedia(customSettings: MediaSettings): Promise<void> {
    this.startMediaPromise =
      this.startMediaPromise ??
      (async (): Promise<void> => {
        try {
          await this.stopLocalMedia();

          const settings: Partial<MediaSettings> = {
            video: customSettings.video ?? true,
            audio: customSettings.audio ?? true,
            virtualBackgroundFilter: customSettings?.virtualBackgroundFilter,
            isNoiseSuppressionEnabled:
              customSettings?.isNoiseSuppressionEnabled,
            videoQuality: customSettings?.videoQuality,
            aspectRatio: customSettings?.aspectRatio,
          };

          if (this.isPublisherInitializing) {
            return undefined;
          }

          this.isPublisherInitializing = true;

          try {
            await this.start(settings);

            this.logger.info('[MediaVonage/startLocalMedia] done', settings);
          } catch (error: unknown) {
            /**
             * In case of an error, we should clean up the publisher but not destroy to avoid endless re-creation
             */
            this.publisher = undefined;
            if (error instanceof Error && error.name.startsWith('OT_')) {
              this.logger.warn('[MediaVonage/startLocalMedia]', error);
            } else {
              this.logger.error('[MediaVonage/startLocalMedia]', error);
            }
          } finally {
            this.isPublisherInitializing = false;
          }
        } finally {
          this.startMediaPromise = undefined;
        }
      })();

    return this.startMediaPromise;
  }

  /**
   * Recreates the publisher: uses access flags as `video`/`audio` for track acquisition.
   * Publish flags are merged from existing {@link PublisherSettings} (mute preserved).
   */
  async restartLocalMedia(): Promise<void> {
    return this.startLocalMedia({
      video: this.hasVideoAccess,
      audio: this.hasAudioAccess,
    });
  }

  private async start(settings: MediaSettings): Promise<void> {
    if (this.publisher) {
      await this.stopLocalMedia();
    }
    await this.createPublisher(settings);
    this.updateSelectedDevicesFromTracks();

    this.fire([
      {
        type: MediaEventType.MediaSourceUpdated,
        data: { source: this.publisher },
      },
    ]);
  }

  private updateSelectedDevicesFromTracks(): void {
    if (this.publisher?.getVideoSource()) {
      this.devicesInstance.setCamera(
        this.publisher.getVideoSource().deviceId as CameraId,
        false,
      );
    }

    if (this.audioTrack) {
      this.devicesInstance.setMicrophone(
        this.audioTrack.getSettings().deviceId as MicrophoneId,
        false,
      );
    }
  }

  private async getVonageFilterFromOurOne(
    ourFilter: VirtualBackgroundFilter,
  ): Promise<VideoFilter | undefined> {
    this.releaseBackgroundObjectUrl();

    const filterObject = { ...ourFilter };

    if (
      ourFilter.type === 'backgroundReplacement' &&
      ourFilter.backgroundImgUrl !== undefined
    ) {
      try {
        filterObject.backgroundImgUrl = await this.resolveBackgroundImgUrl(
          ourFilter.backgroundImgUrl,
        );
      } catch (error: unknown) {
        // Don't apply a background whose image failed to load — Vonage's
        // media processor crashes later (null track on toggle) if given a
        // broken image. Skip it and fall back to no background.
        this.logger.warn(
          '[MediaVonage/getVonageFilterFromOurOne] failed to load background image, skipping',
          error,
        );

        return undefined;
      }
    }

    return filterObject as VideoFilter;
  }

  private async resolveBackgroundImgUrl(url: string): Promise<string> {
    if (!/^https?:\/\//.test(url)) {
      return `/v2${url}`;
    }

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch background image: ${response.status} ${response.statusText}`,
      );
    }

    const blob = await response.blob();

    return this.blobToImageDataUrl(blob);
  }

  private async blobToImageDataUrl(blob: Blob): Promise<string> {
    const mimeType = blob.type.startsWith('image/') ? blob.type : 'image/jpeg';
    const typedBlob =
      blob.type === mimeType ? blob : blob.slice(0, blob.size, mimeType);

    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () =>
        reject(reader.error ?? new Error('Failed to read background image'));
      reader.readAsDataURL(typedBlob);
    });
  }

  private releaseBackgroundObjectUrl(): void {
    if (this.backgroundObjectUrl !== undefined) {
      URL.revokeObjectURL(this.backgroundObjectUrl);
      this.backgroundObjectUrl = undefined;
    }
  }

  private isSameVirtualBackground(filter: VirtualBackgroundFilter): boolean {
    const current = this.appliedVirtualBackground;

    if (current === null || current.type !== filter.type) {
      return false;
    }

    return filter.type === 'backgroundBlur'
      ? current.blurStrength === filter.blurStrength
      : current.backgroundImgUrl === filter.backgroundImgUrl;
  }

  get isVirtualBackgroundEnabled(): boolean {
    return this.hasVideo && !!this.publisherSettings.videoFilter;
  }

  async applyVirtualBackground(
    filter: VirtualBackgroundFilter | null,
  ): Promise<void> {
    const opentok = await this.opentokClient;

    if (!this.publisher || !opentok!.hasMediaProcessorSupport()) {
      return;
    }

    if (filter === null) {
      await this.publisher.clearVideoFilter();

      this.releaseBackgroundObjectUrl();
      this.appliedVirtualBackground = null;
      this.publisherSettings.videoFilter = undefined;
    } else if (!this.isSameVirtualBackground(filter)) {
      const vonageFilter = await this.getVonageFilterFromOurOne(filter);

      if (!vonageFilter) {
        return;
      }

      await this.publisher.applyVideoFilter(vonageFilter);

      this.appliedVirtualBackground = filter;
      this.publisherSettings.videoFilter = vonageFilter;
    }

    this.fire([{ type: MediaEventType.MediaStreamVideoTrackUpdated }]);
  }

  async toggleAdvancedNoiseSuppression(enable: boolean): Promise<void> {
    const opentok = await this.opentokClient;

    const prefix = '[MediaVonage/toggleAdvancedNoiseSuppression]';

    /**
     * From documentation: "Audio filters are only supported in recent versions of Chrome, Electron, Opera, and Edge.
     * They are not supported in other (non-Chromium-based) browsers or on iOS.
     * @docs https://tokbox.com/developer/sdks/js/reference/OT.html
     */
    if (!opentok!.hasMediaProcessorSupport()) {
      return;
    }

    if (!this.publisher) {
      return;
    }

    if (this.publisherSettings.useMicrophone === false) {
      this.logger.warn(prefix, 'publisher was created without microphone.');
      return;
    }

    /**
     * Avoid triggering Vonage OT_NOT_SUPPORTED exceptions when the publisher was created without an audio source
     */
    if (!this.hasAudioSource) {
      this.logger.warn(prefix, 'publisher has no audio source. Skipping.');
      return;
    }

    if (!enable) {
      try {
        await this.publisher.clearAudioFilter();
        this.publisherSettings.audioFilter = undefined;
        this.logger.info(`${prefix} cleared AdvancedNoiseSuppression`);
      } catch (error) {
        this.logger.error(
          `${prefix} failed to clear AdvancedNoiseSuppression`,
          error,
        );
        throw error;
      }

      return;
    }

    try {
      // if it's already applied, do nothing
      if (this.publisherSettings.audioFilter) {
        this.logger.info(`${prefix} AdvancedNoiseSuppression already applied`);
        return;
      }

      this.publisherSettings.audioFilter = { type: 'advancedNoiseSuppression' };
      await this.publisher.applyAudioFilter({
        type: 'advancedNoiseSuppression',
      });
      this.logger.info(
        `${prefix} successfully applied AdvancedNoiseSuppression`,
      );
    } catch (error) {
      this.publisherSettings.audioFilter = undefined;
      this.logger.error(
        `${prefix} failed to apply AdvancedNoiseSuppression`,
        error,
      );
      throw error;
    }
  }

  private getVideoQualityProfile(
    videoQuality: VideoQuality,
    aspectRatio: AspectRatio,
  ): VideoQualityConstraint {
    return aspectRatio === '16:9'
      ? VideoQualityProfilesWide[videoQuality]
      : VideoQualityProfiles[videoQuality];
  }

  async changeVideoQuality(
    videoQuality: VideoQuality,
    aspectRatio: AspectRatio = this.publisherSettings.aspectRatio ?? '16:9',
  ): Promise<void> {
    await this.changeVideoConstraints(
      this.getVideoQualityProfile(videoQuality, aspectRatio),
    );

    this.publisherSettings.aspectRatio = aspectRatio;
    this.publisherSettings.videoQuality = videoQuality;
  }

  async initScreenSharePublisher(): Promise<void> {
    const opentok = await this.opentokClient!;

    await new Promise<void>((resolve, reject) => {
      const prefix = '[MediaVonage/initScreenSharePublisher]';

      const publishOptions: PublisherProperties = {
        videoContentHint: 'detail',
        videoSource: 'screen',
        insertMode: 'append',
        publishAudio: true,
        publishVideo: true,
        fitMode: 'contain',
        maxResolution: { width: 1920, height: 1080 },
        showControls: false,
        insertDefaultUI: false,
        disableAudioInputDeviceManagement: true,
        scalableScreenshare: true, // this feature in beta
      };

      const screenPublisher = opentok.initPublisher(
        this.screenElement,
        publishOptions,
        (error) => {
          if (error) {
            this.logger.error(prefix, error);
            this.screenPublisher = undefined;
            reject(error);
          } else {
            this.logger.info(`${prefix} done`);

            this.fire([
              {
                type: MediaEventType.MediaStreamScreenTrackStarted,
              },
              {
                type: MediaEventType.MediaStreamScreenTrackUpdated,
              },
            ]);

            resolve();
          }
        },
      );

      screenPublisher.on('videoDimensionsChanged', function (event) {
        if (screenPublisher.element) {
          screenPublisher.element.style.width = `${event.newValue.width}px`;
          screenPublisher.element.style.height = `${event.newValue.height}px`;
        }
      });

      screenPublisher.on('accessDenied', (e) => {
        this.logger.error(prefix, 'accessDenied', e);
        reject(e);
      });

      screenPublisher.on('destroyed', () => {
        this.logger.info('[MediaVonage/stopScreenSharing] done');
        this.fire([
          { type: MediaEventType.MediaStreamScreenTrackEnded },
          {
            type: MediaEventType.MediaStreamScreenTrackUpdated,
          },
        ]);
      });

      this.screenPublisher = screenPublisher;
    });
  }

  // TODO consider adding notification of changes to selected devices
  private async onSelectedDevicesUpdate(
    e: SelectedDevicesChangeEvent,
  ): Promise<void> {
    // If local media is starting skip to avoid race condition to update publisher settings.
    if (this.isPublisherInitializing || !this.publisher) {
      return;
    }

    const field = e.data.field;
    const newId = e.data.value;
    const explicitId = e.data.explicitId;

    try {
      const publisherVideoSourceId = this.publisher?.getVideoSource().deviceId;
      const publisherAudioSourceId = this.audioTrack?.getSettings().deviceId;
      if (
        field === 'cameraId' &&
        newId &&
        publisherVideoSourceId !== null &&
        publisherVideoSourceId !== newId
      ) {
        await this._changeCamera(newId as CameraId);
        await this.toggleVideo(false);
      }
      if (
        field === 'microphoneId' &&
        publisherAudioSourceId !== undefined &&
        publisherAudioSourceId !== explicitId
      ) {
        await this._changeMicrophone(explicitId ?? (newId as MicrophoneId));
      }

      this.logger.info('[MediaVonage/onSelectedDevicesUpdate] done', {
        field,
        newId,
        explicitId,
      });
    } catch (error) {
      this.logger.error('[MediaVonage/onSelectedDevicesUpdate]', {
        field,
        newId,
        explicitId,
        error,
      });
    }
  }

  private async audioFallbackEnabled(): Promise<void> {
    this.fire([{ type: MediaEventType.MediaAudioFallbackEnabled }]);
    this.logger.info('[MediaVonage/audioFallbackEnabled] done');
  }

  private async audioFallbackDisabled(): Promise<void> {
    this.fire([{ type: MediaEventType.MediaAudioFallbackDisabled }]);
    this.logger.info('[MediaVonage/audioFallbackDisabled] done');
  }

  private async videoDisableWarningEnabled(): Promise<void> {
    this.fire([{ type: MediaEventType.MediaVideoDisableWarningEnabled }]);
  }

  private async videoDisableWarningDisabled(): Promise<void> {
    this.fire([{ type: MediaEventType.MediaVideoDisableWarningDisabled }]);
  }

  private getCurrentVideoConstraints(): VideoQualityConstraint {
    return this.getVideoQualityProfile(
      this.publisherSettings.videoQuality ?? getDefaultVideoQuality(),
      this.publisherSettings.aspectRatio ?? '16:9',
    );
  }

  private getMediaSetStatus: GetMediaSetStatusType = async (
    settings,
    getMedia,
    getError,
  ) => {
    try {
      const media = await getMedia();
      this.setMediaAccessStatus(settings, MediaAccessStatus.Allowed);
      this.setMediaAccessError(settings, undefined);
      return media;
    } catch (error) {
      const mappedError = getError(error);
      const status = [
        MediaAccessError.PermissionDenied,
        MediaAccessError.PermissionDeniedBySystem,
        MediaAccessError.DeviceNotFound,
      ].includes(mappedError)
        ? MediaAccessStatus.Denied
        : MediaAccessStatus.Error;
      this.setMediaAccessStatus(settings, status);
      this.setMediaAccessError(settings, mappedError);

      throw error;
    }
  };

  private async handlePermissionChanges(): Promise<void> {
    try {
      const [cameraPermission, microphonePermission] = await Promise.all([
        navigator.permissions.query({ name: 'camera' as PermissionName }),
        navigator.permissions.query({ name: 'microphone' as PermissionName }),
      ]);

      // Check permissions on init and update access info accordingly
      this.updateAccessInfo('video', cameraPermission.state);
      this.updateAccessInfo('audio', microphonePermission.state);

      cameraPermission.addEventListener('change', () => {
        this.updateAccessInfo('video', cameraPermission.state);
      });

      microphonePermission.addEventListener('change', () => {
        this.updateAccessInfo('audio', microphonePermission.state);
      });
    } catch {
      // Ignore the error — the browser probably doesn't support this API.
    }
  }

  private updateAccessInfo(
    media: 'video' | 'audio',
    state: PermissionState,
  ): void {
    const settings = { [media]: true } as unknown as MediaSettings;

    if (state === 'prompt') {
      this.setMediaAccessStatus(settings, undefined);
      this.setMediaAccessError(settings, undefined);
    } else if (state === 'denied') {
      this.setMediaAccessStatus(settings, MediaAccessStatus.Denied);
      this.setMediaAccessError(settings, MediaAccessError.PermissionDenied);
    } else if (
      this[`${media}AccessError`] !== MediaAccessError.PermissionDeniedBySystem
    ) {
      this.setMediaAccessStatus(settings, MediaAccessStatus.Allowed);
      this.setMediaAccessError(settings, undefined);
    }
  }

  private setMediaAccessStatus(
    settings: MediaSettings,
    status: MediaAccessStatus | undefined,
  ): void {
    const currentStatus: CurrentAccessStatus = {};
    if (Boolean(settings.video) && this.videoAccessStatus !== status) {
      this.videoAccessStatus = status;
      currentStatus.video = status;
    }
    if (Boolean(settings.audio) && this.audioAccessStatus !== status) {
      this.audioAccessStatus = status;
      currentStatus.audio = status;
    }
    if (Object.keys(currentStatus).length > 0) {
      void this.devicesInstance.updatedAccessStatus(); // need to grab devices again as only when access allowed we can see labels
      this.fire([{ type: MediaEventType.MediaAccessStatusChange }]);
    }
  }

  private setMediaAccessError(
    settings: MediaSettings,
    error: MediaAccessError | undefined,
  ): void {
    this.videoAccessError = Boolean(settings.video)
      ? error
      : this.videoAccessError;
    this.audioAccessError = Boolean(settings.audio)
      ? error
      : this.audioAccessError;
  }

  private async _onSelectedDevicesUpdate(e: FireEvent): Promise<void> {
    void this.onSelectedDevicesUpdate(e as SelectedDevicesChangeEvent);
  }

  private _onMediaAccessDeny(event: FireEvent): void {
    const { audio, video } = (event as MediaAccessDeniedEvent).data;

    if (audio) {
      this.updateAccessInfo('audio', 'denied');
    }
    if (video) {
      this.updateAccessInfo('video', 'denied');
    }
  }

  private addEventListeners(): void {
    this.unsubscribeList?.push(
      this.on(
        [MediaEventType.SelectedDevicesChange],
        this._onSelectedDevicesUpdate.bind(this),
      ),
    );
    this.unsubscribeList?.push(
      this.on(
        [MediaEventType.MediaAccessDenied],
        this._onMediaAccessDeny.bind(this),
      ),
    );
  }
}

export const media = new Media();
